/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect } from 'react';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ClerkProvider } from '@clerk/react';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './app/layout';
import { AppRouter } from './app/router';
import { DevToolbar } from './components/DevToolbar';
import { ScrollToHash } from './components/shell/ScrollToHash';
import { ScrollToTop } from './components/utils/ScrollToTop';
import { DefaultOrgResolver } from './components/utils/DefaultOrgResolver';
import { ErrorBoundary } from './components/ErrorBoundary';
import { UpdateBanner } from './components/shell/UpdateBanner';

import { SHOULD_USE_MOCKS, CLERK_PUBLISHABLE_KEY } from './lib/clerk';
import { HelmetProvider } from 'react-helmet-async';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 401 || error?.response?.status === 403 || error?.response?.status === 404 || error?.response?.status === 405) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

function AppContent({ isPrerendering }: { isPrerendering: boolean }) {
  const showDevToolbar = import.meta.env.DEV || SHOULD_USE_MOCKS || import.meta.env.VITE_ENABLE_DEVTOOLS === 'true';

  const { isLoading } = useTenant();

  useEffect(() => {
    if (isPrerendering && !isLoading) {
      // Signal to the prerenderer that the page is ready after contexts mount and load
      const timer = setTimeout(() => {
        document.dispatchEvent(new Event('prerender-ready'));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isPrerendering, isLoading]);

  return (
    <ErrorBoundary>
      <ScrollToHash />
      <ScrollToTop />
      <UpdateBanner />
      <DefaultOrgResolver />
      <Layout>
        <AppRouter />
      </Layout>
      {showDevToolbar && <DevToolbar />}
    </ErrorBoundary>
  );
}

interface AppProps {
  isPrerendering?: boolean;
}

export default function App({ isPrerendering = false }: AppProps) {
  // Immediate client-side safety net: redirect www.orgsets.com to orgsets.com
  if (typeof window !== 'undefined' && !isPrerendering && window.location.hostname.toLowerCase() === 'www.orgsets.com') {
    window.location.replace(`https://orgsets.com${window.location.pathname}${window.location.search}${window.location.hash}`);
    return null;
  }

  // Swap to MemoryRouter during JSDOM build so it parses location correctly
  const RouterComponent = isPrerendering ? MemoryRouter : BrowserRouter;
  const routerProps = isPrerendering
    ? { initialEntries: [window.location.pathname] }
    : {};

  // Bypass Clerk completely if we are running in mock mode
  if (SHOULD_USE_MOCKS) {
    return (
      <ErrorBoundary>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <RouterComponent {...routerProps}>
              <TenantProvider>
                <AuthProvider>
                  <AppContent isPrerendering={isPrerendering} />
                  <Toaster position="bottom-right" />
                </AuthProvider>
              </TenantProvider>
            </RouterComponent>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    );
  }

  // Production/Live branch with active Clerk Authentication
  const clerkKey = CLERK_PUBLISHABLE_KEY || (isPrerendering ? 'pk_test_ZHVtbXlrZXk0YnVpbGQ=' : '');

  return (
    <ErrorBoundary>
      <ClerkProvider publishableKey={clerkKey}>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <RouterComponent {...routerProps}>
              <TenantProvider>
                <AuthProvider>
                  <AppContent isPrerendering={isPrerendering} />
                  <Toaster position="bottom-right" />
                </AuthProvider>
              </TenantProvider>
            </RouterComponent>
          </QueryClientProvider>
        </HelmetProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
}